import React, { useState, useEffect } from 'react';
import moment from 'moment';
import 'moment/locale/en-gb';
import {
  Switch,
  Checkbox,
  Select,
  DatePicker,
  Radio,
  Button,
  Typography,
  Divider,
} from 'antd';
import { CloseCircleOutlined } from '@ant-design/icons';

const { Title } = Typography;
const { Option } = Select;

const PeriodGroupContent = ({
  selectedGroup,
  setSelectedGroup,
  selectedTimePeriod,
  setSelectedTimePeriod,
  selectedDateRange,
  setSelectedDateRange,
}) => {
  const [isClearVisible, setIsClearVisible] = useState(false);
  const [groupingEnabled, setGroupingEnabled] = useState(true);
  const [applyToAllTabs, setApplyToAllTabs] = useState(false);
  const [selectAll, setSelectAll] = useState(true);
  const [selectIndeterminate, setSelectIndeterminate] = useState(false);
  const [groupItems, setGroupItems] = useState([]);

  // Use this useEffect to determine if the clear button should be visible
  useEffect(() => {
    setIsClearVisible(
      selectedGroup !== 'none' ||
      selectedTimePeriod !== 'Manual selection' ||
      (selectedDateRange && selectedDateRange[0] !== null) ||
      (selectedDateRange && selectedDateRange[1] !== null)
    );
  }, [selectedGroup, selectedTimePeriod, selectedDateRange]);

  // This effect handles the dynamic group items based on the date range
  useEffect(() => {
    if (!selectedDateRange || !selectedDateRange[0] || !selectedDateRange[1] || !['year', 'quarter', 'month'].includes(selectedGroup)) {
      setGroupItems([]);
      setSelectAll(true);
      setSelectIndeterminate(false);
      return;
    }

    let start = moment(selectedDateRange[0]);
    let end = moment(selectedDateRange[1]);
    const items = [];

    if (selectedGroup === 'year') {
      while (start.isBefore(end) || start.isSame(end, 'year')) {
        items.push(start.format('YYYY'));
        start = start.add(1, 'year');
      }
    } else if (selectedGroup === 'quarter') {
      while (start.isBefore(end) || start.isSame(end, 'quarter')) {
        const year = start.year();
        const quarter = Math.floor(start.month() / 3) + 1;
        items.push(`Q${quarter} ${year}`);
        start = start.add(1, 'quarter');
      }
    } else if (selectedGroup === 'month') {
      while (start.isBefore(end) || start.isSame(end, 'month')) {
        items.push(start.format('MMM'));
        start = start.add(1, 'month');
      }
    }
    setGroupItems(items);
    setSelectAll(false);
    setSelectIndeterminate(true);
  }, [selectedDateRange, selectedGroup]);

  const handleTimePeriodChange = (value) => {
    setSelectedTimePeriod(value);
    const today = moment();
    let newFromDate = null;
    let newToDate = null;

    switch (value) {
      case 'This year':
        newFromDate = today.startOf('year');
        newToDate = today.endOf('year');
        break;
      // ... (rest of your cases)
      default:
        newFromDate = null;
        newToDate = null;
    }

    // Now, set the date range in the parent state directly
    setSelectedDateRange(newFromDate && newToDate ? [newFromDate.toDate(), newToDate.toDate()] : null);
  };

  const handleFromDateChange = (date) => {
    setSelectedDateRange([date?.toDate(), selectedDateRange?.[1] ? selectedDateRange[1] : null]);
    setSelectedTimePeriod('Manual selection');
  };

  const handleToDateChange = (date) => {
    setSelectedDateRange([selectedDateRange?.[0] ? selectedDateRange[0] : null, date?.toDate()]);
    setSelectedTimePeriod('Manual selection');
  };

  const handleClear = () => {
    setSelectedGroup('none');
    setSelectedTimePeriod('Manual selection');
    setSelectedDateRange(null);
    setSelectAll(true);
    setSelectIndeterminate(false);
  };

  return (
    <div style={{ padding: '10px', maxHeight: '500px', overflowY: 'auto' }}>
      <Title level={5}>Group by period</Title>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <span>Enable grouping</span>
        <Switch checked={groupingEnabled} onChange={setGroupingEnabled} />
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <span>Apply filter to all tabs</span>
        <Switch checked={applyToAllTabs} onChange={setApplyToAllTabs} />
      </div>

      <Divider style={{ margin: '10px 0' }} />

      <Radio.Group
        onChange={(e) => setSelectedGroup(e.target.value)}
        value={selectedGroup}
        style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}
      >
        <Radio value="year">Year</Radio>
        <div style={{ paddingLeft: 20 }}>
          <Radio value="quarter">Quarter</Radio>
          <div style={{ paddingLeft: 20 }}>
            <Radio value="month">Month</Radio>
          </div>
        </div>
      </Radio.Group>

      <Divider style={{ margin: '10px 0' }} />

      <Checkbox
        checked={selectAll}
        indeterminate={selectIndeterminate}
        onChange={(e) => {
          setSelectAll(e.target.checked);
          setSelectIndeterminate(false);
        }}
      >
        (Select all)
      </Checkbox>

      {groupItems.length > 0 && (
        <div style={{ margin: '10px 0' }}>
          {groupItems.map((item) => (
            <div key={item}><Checkbox checked>{item}</Checkbox></div>
          ))}
        </div>
      )}

      <div style={{ marginTop: 10 }}>
        <label style={{ display: 'block', marginBottom: 4 }}>Select a time period</label>
        <Select
          value={selectedTimePeriod}
          onChange={handleTimePeriodChange}
          style={{ width: '100%' }}
          showSearch
        >
          <Option value="Manual selection">Manual selection</Option>
          <Option value="This year">This year</Option>
          <Option value="Next 12 months">Next 12 months</Option>
          <Option value="Since beginning of month">Since beginning of month</Option>
          <Option value="Since beginning of this year">Since beginning of this year</Option>
          <Option value="Last 2 weeks and next 4 weeks">Last 2 weeks and next 4 weeks</Option>
          <Option value="Last 4 weeks and next 8 weeks">Last 4 weeks and next 8 weeks</Option>
          <Option value="Last 1 and next 3 months">Last 1 and next 3 months</Option>
          <Option value="Last 3 and next 9 months">Last 3 and next 9 months</Option>
          <Option value="Last 6 and next 12 months">Last 6 and next 12 months</Option>
          <Option value="This year and next 2 years">This year and next 2 years</Option>
          <Option value="Until current month">Until current month</Option>
          <Option value="Until current month - 2">Until current month - 2</Option>
          <Option value="2019">2019</Option>
          <Option value="2020">2020</Option>
        </Select>
      </div>

      <div style={{ marginTop: 10 }}>
        <label>From</label>
        <DatePicker
          style={{ width: '100%' }}
          value={selectedDateRange?.[0] ? moment(selectedDateRange[0]) : null}
          onChange={handleFromDateChange}
          placeholder="<program start>"
        />
      </div>

      <div style={{ marginTop: 10 }}>
        <label>To</label>
        <DatePicker
          style={{ width: '100%' }}
          value={selectedDateRange?.[1] ? moment(selectedDateRange[1]) : null}
          onChange={handleToDateChange}
          placeholder="<program end>"
        />
      </div>

      {isClearVisible && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 15 }}>
          <Button
            type="text"
            onClick={handleClear}
            icon={<CloseCircleOutlined />}
            style={{ padding: 0, color: 'blue' }}
          >
            Clear
          </Button>
        </div>
      )}
    </div>
  );
};

export default PeriodGroupContent;